import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';

/**
 * Firebase on the web, with the same accounts as the phone (project routr-2026). The web app's config comes from
 * VITE_ variables (.env.example): no Firebase web app exists yet, and making one is a console step (README).
 * With VITE_USE_EMULATORS=true everything runs on the local emulators: sign-in, the database and the functions.
 */
const env = import.meta.env;

export const usingEmulators = env.VITE_USE_EMULATORS === 'true';

const config = {
  apiKey: env.VITE_FIREBASE_API_KEY || (usingEmulators ? 'emulator-key' : ''),
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || 'routr-2026.firebaseapp.com',
  projectId: env.VITE_FIREBASE_PROJECT_ID || 'routr-2026',
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || 'routr-2026.firebasestorage.app',
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || '486444732380',
  appId: env.VITE_FIREBASE_APP_ID || (usingEmulators ? '1:486444732380:web:emulator' : ''),
};

/** What's missing before the page can talk to Firebase: said on the sign-in page, not thrown. */
export const missingConfig: string[] = usingEmulators
  ? []
  : (['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_APP_ID'] as const).filter((k) => !env[k]);

export const app: FirebaseApp = initializeApp(config);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const functions: Functions = getFunctions(app, env.VITE_FUNCTIONS_REGION || 'europe-west1');

if (usingEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

export const mapsKey: string = env.VITE_MAPS_KEY || '';
export const mapId: string = env.VITE_MAP_ID || '';
